import React, { FC, useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { PageLink, PageTitle } from "../../../_metronic/layout/core";
import { Breadcrumb } from "../../../_metronic/layout/components/header/Breadcrumb";
import { AlertWidget, ModalWidget, TableLoader } from "../../UI";
import {
  CUSTOM_TYPES,
  deleteCustomField,
  FieldType,
  getProfileFields,
  initialLibrary,
  isDropdown,
  keyFromLabel,
  LibraryField,
  saveCustomFields,
  typeLabel,
} from "./profileFieldsLibrary-hooks";
import { uuid } from "../../../hooks/utils";
import "./ProfileFieldsLibrary.css";

type SortDir = null | "asc" | "desc";

const ProfileFieldsLibrary: FC = () => {
  const intl = useIntl();

  const [library, setLibrary] = useState<LibraryField[]>(initialLibrary);
  // Snapshot of the custom fields as last loaded from the server — the baseline
  // we diff against on Save to know what to create / update / delete.
  const [baseline, setBaseline] = useState<LibraryField[]>([]);
  const [loader, setLoader] = useState(true);
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState("");
  const [sortDir, setSortDir] = useState<SortDir>(null);
  const [alertMessage, setAlertMessage] = useState<string | boolean>("");

  const refresh = () => {
    setLoader(true);
    return getProfileFields()
      .then((fields) => {
        setLibrary(fields);
        setBaseline(fields.filter((f) => !f.system).map((f) => ({ ...f })));
      })
      .catch((e) => toast(e?.message || "Unable to load profile fields."))
      .finally(() => setLoader(false));
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // add-new-field form
  const [showAddField, setShowAddField] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  const [newType, setNewType] = useState<FieldType>("text");
  const [newOptions, setNewOptions] = useState<string[]>([]);
  const [newOptNew, setNewOptNew] = useState("");

  // inline edit
  const [editKey, setEditKey] = useState<string | null>(null);
  const [editLabel, setEditLabel] = useState("");
  const [editOptions, setEditOptions] = useState<string[]>([]);
  const [editOptNew, setEditOptNew] = useState("");

  // modals
  const [removeField, setRemoveField] = useState<LibraryField | null>(null);
  const [optionToRemove, setOptionToRemove] = useState<{
    option: string;
    count: number;
  } | null>(null);

  const toast = (msg: string) => setAlertMessage(msg);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = library.filter((f) => !q || f.label.toLowerCase().includes(q));
    if (sortDir) {
      list = [...list].sort((a, b) => a.label.localeCompare(b.label));
      if (sortDir === "desc") list.reverse();
    }
    return list;
  }, [library, search, sortDir]);

  const toggleSort = () => setSortDir((d) => (d === "asc" ? "desc" : "asc"));

  /* ---------- add field ---------- */
  const resetAddForm = () => {
    setNewLabel("");
    setNewType("text");
    setNewOptions([]);
    setNewOptNew("");
  };

  const addNewOption = () => {
    const v = newOptNew.trim();
    if (!v || newOptions.includes(v)) return;
    setNewOptions([...newOptions, v]);
    setNewOptNew("");
  };

  const addField = () => {
    const label = newLabel.trim();
    if (!label) {
      toast("Enter a field label.");
      return;
    }
    const key = keyFromLabel(
      label,
      library.map((f) => f.key)
    );
    const field: LibraryField = {
      id: uuid(),
      key,
      label,
      type: newType,
      system: false,
      builtIn: false,
      options: isDropdown(newType) ? newOptions : [],
      deprecated: false,
      defaultRequired: false,
      usedIn: 0,
      optionUsage: {},
    };
    setLibrary([...library, field]);
    resetAddForm();
    setShowAddField(false);
    toast(`"${label}" added — press Save to apply your changes.`);
  };

  /* ---------- reactivate / delete (local until Save) ---------- */
  const reactivate = (field: LibraryField) => {
    setLibrary(
      library.map((f) =>
        f.key === field.key ? { ...f, deprecated: false } : f
      )
    );
    toast(`"${field.label}" reactivated — press Save to apply.`);
  };

  const deleteField = (field: LibraryField) => {
    setLibrary(library.filter((f) => f.key !== field.key));
    if (editKey === field.key) closeEdit();
    setRemoveField(null);
    toast(`"${field.label}" removed — press Save to apply.`);
  };

  /* ---------- inline edit ---------- */
  const openEdit = (field: LibraryField) => {
    if (editKey === field.key) {
      closeEdit();
      return;
    }
    setEditKey(field.key);
    setEditLabel(field.label);
    setEditOptions([...field.options]);
    setEditOptNew("");
  };

  const closeEdit = () => {
    setEditKey(null);
    setEditLabel("");
    setEditOptions([]);
    setEditOptNew("");
  };

  const addEditOption = () => {
    const v = editOptNew.trim();
    if (!v || editOptions.includes(v)) return;
    setEditOptions([...editOptions, v]);
    setEditOptNew("");
  };

  const requestRemoveEditOption = (field: LibraryField, option: string) => {
    const count = field.optionUsage?.[option] || 0;
    if (count > 0) {
      setOptionToRemove({ option, count });
    } else {
      setEditOptions(editOptions.filter((o) => o !== option));
    }
  };

  const confirmRemoveEditOption = () => {
    if (!optionToRemove) return;
    const opt = optionToRemove.option;
    // Drop from the draft; the removal (and clearing of company data) is persisted on Save.
    setEditOptions(editOptions.filter((o) => o !== opt));
    setOptionToRemove(null);
  };

  const saveEdit = (field: LibraryField) => {
    const label = editLabel.trim();
    if (!label) {
      toast("Field label is required.");
      return;
    }
    setLibrary(
      library.map((f) =>
        f.key === field.key
          ? {
              ...f,
              label,
              options: isDropdown(f.type) ? [...editOptions] : f.options,
            }
          : f
      )
    );
    closeEdit();
    toast("Field updated — press Save to apply your changes.");
  };

  /* ---------- persist all pending changes ---------- */
  const optionsChanged = (a: string[] = [], b: string[] = []) =>
    JSON.stringify(a) !== JSON.stringify(b);

  // Diff the working list against the server baseline.
  const computePending = () => {
    const currentCustom = library.filter((f) => !f.system);
    const baseById = new Map(baseline.map((f) => [f.id, f]));
    const currentIds = new Set(currentCustom.map((f) => f.id));
    const toCreate = currentCustom.filter((f) => !baseById.has(f.id));
    const toUpdate = currentCustom.filter((f) => {
      const b = baseById.get(f.id);
      return (
        b &&
        (b.label !== f.label ||
          b.deprecated !== f.deprecated ||
          optionsChanged(b.options, f.options))
      );
    });
    const toDelete = baseline.filter((b) => !currentIds.has(b.id));
    return { toCreate, toUpdate, toDelete };
  };

  const hasUnsavedChanges = (() => {
    const { toCreate, toUpdate, toDelete } = computePending();
    return toCreate.length > 0 || toUpdate.length > 0 || toDelete.length > 0;
  })();

  // Warn before leaving (tab close, refresh, or navigating to another URL) while
  // there are unsaved changes. The dialog ("Leave site? / Changes you made may
  // not be saved.") is rendered by the browser and cannot be customised.
  useEffect(() => {
    if (!hasUnsavedChanges) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [hasUnsavedChanges]);

  const handleSave = async () => {
    if (busy) return;
    const { toCreate, toUpdate, toDelete } = computePending();

    if (!toCreate.length && !toUpdate.length && !toDelete.length) {
      toast("No changes to save.");
      return;
    }

    setBusy(true);
    try {
      // New + edited fields go in a single POST (backend upserts by profile_field_id).
      const toSave = [...toCreate, ...toUpdate];
      const ops: Promise<any>[] = [];
      if (toSave.length) {
        ops.push(
          saveCustomFields(
            toSave.map((f) => ({
              id: f.id,
              label: f.label,
              type: f.type,
              options: f.options,
            }))
          )
        );
      }
      toDelete.forEach((f) => ops.push(deleteCustomField(f.id)));
      await Promise.all(ops);
      await refresh();
      toast("Profile fields saved.");
    } catch (e: any) {
      toast(e?.message || "Could not save changes. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const handleCancel = () => {
    // Return to wherever we came from (e.g. the company group form's "Manage
    // library" link passes ?redirect_uri=...). Only honour same-origin relative
    // paths; otherwise fall back to Setup.
    const redirect = new URLSearchParams(window.location.search).get(
      "redirect_uri"
    );
    const safe =
      redirect && /^\/(?!\/)/.test(redirect) ? redirect : "/setup.html";
    window.location.href = safe;
  };

  /* ---------- breadcrumb ---------- */
  const breadcrumbs: Array<PageLink> = [
    {
      title: intl.formatMessage({ id: "MENU.SETUP" }),
      path: "/setup.html",
      isSeparator: true,
      isActive: false,
    },
    {
      title: intl.formatMessage({ id: "MENU.PROFILEFIELDSLIBRARY" }),
      path: "/profile-fields-library",
      isSeparator: false,
      isActive: false,
    },
  ];

  const optionSummary = (field: LibraryField) => {
    if (!isDropdown(field.type) || field.options.length === 0) return null;
    const shown = field.options.slice(0, 4).join(", ");
    const extra = field.options.length - 4;
    return extra > 0 ? `${shown} +${extra} more` : shown;
  };

  const renderEditPanel = (field: LibraryField) => (
    <tr>
      <td colSpan={3} style={{ padding: "0 8px 16px" }}>
        <div className="pfl-edit-panel">
          <h3
            className="mb-1"
            style={{ fontSize: 15, fontWeight: 700, color: "#2f353c" }}
          >
            Edit field
          </h3>
          <p style={{ margin: "0 0 16px", fontSize: 12, color: "#9aa1a8" }}>
            Changes apply to every group that uses this field.
          </p>

          <label
            className="form-label"
            style={{ fontSize: 12, color: "#8a929a", fontWeight: 600 }}
          >
            Label
          </label>
          <input
            className="form-control form-control-sm mb-4"
            style={{ maxWidth: 440 }}
            value={editLabel}
            onChange={(e) => setEditLabel(e.target.value)}
          />

          <label
            className="form-label d-block"
            style={{ fontSize: 12, color: "#8a929a", fontWeight: 600 }}
          >
            Format
          </label>
          <div className="d-flex align-items-center gap-2 mb-4">
            <span className="pfl-type-badge">{typeLabel(field.type)}</span>
            <span style={{ fontSize: 11.5, color: "#b1b8bf" }}>
              Format is fixed once a field is created.
            </span>
          </div>

          {isDropdown(field.type) && (
            <>
              <label
                className="form-label d-block"
                style={{ fontSize: 12, color: "#8a929a", fontWeight: 600 }}
              >
                Options
              </label>
              <div
                className="d-flex flex-column gap-2 mb-3"
                style={{ maxWidth: 440 }}
              >
                {editOptions.map((o) => {
                  const count = field.optionUsage?.[o] || 0;
                  return (
                    <div key={o} className="pfl-option-row">
                      <span
                        style={{ flex: 1, fontSize: 13.5, color: "#3a4149" }}
                      >
                        {o}
                      </span>
                      <span
                        title="Companies with this option selected"
                        className={`pfl-use-pill ${
                          count > 0 ? "in-use" : "unused"
                        }`}
                      >
                        {count > 0 ? `${count} in use` : "unused"}
                      </span>
                      <span
                        title="Remove option"
                        className="pfl-chip-x"
                        style={{
                          color: "#c0392b",
                          fontWeight: 700,
                          fontSize: 14,
                          cursor: "pointer",
                        }}
                        onClick={() => requestRemoveEditOption(field, o)}
                      >
                        ×
                      </span>
                    </div>
                  );
                })}
              </div>
              <div className="d-flex gap-2 mb-4" style={{ maxWidth: 440 }}>
                <input
                  className="form-control form-control-sm"
                  placeholder="Add an option"
                  value={editOptNew}
                  onChange={(e) => setEditOptNew(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && addEditOption()}
                />
                <button
                  className="btn btn-sm btn-light-primary"
                  onClick={addEditOption}
                >
                  Add
                </button>
              </div>
            </>
          )}

          {field.usedIn > 0 && (
            <div className="pfl-blast mb-4">
              This field is used in {field.usedIn} group
              {field.usedIn === 1 ? "" : "s"}. Saving applies your changes
              everywhere it's used.
            </div>
          )}

          <div className="d-flex gap-3">
            <button
              className="btn btn-sm btn-ll"
              onClick={() => saveEdit(field)}
            >
              Save Changes
            </button>
            <button
              className="btn btn-sm btn-secondary cancelButton"
              onClick={closeEdit}
            >
              Cancel
            </button>
          </div>
        </div>
      </td>
    </tr>
  );

  return (
    <>
      <Breadcrumb
        pageBreadcrumbs={breadcrumbs}
        activePageName={"profile_fields_library"}
      />
      <PageTitle activePageName={"profile_fields_library"}></PageTitle>

      {alertMessage && <AlertWidget message={alertMessage} />}

      {/* delete-option-in-use confirmation */}
      <ModalWidget
        title="Delete option in use?"
        modalBody={
          <p style={{ fontSize: 15 }}>
            {optionToRemove && (
              <>
                "{optionToRemove.option}" is currently selected by{" "}
                {optionToRemove.count} compan
                {optionToRemove.count === 1 ? "y" : "ies"}. Deleting it will
                clear that response from those records — this cannot be undone.
              </>
            )}
          </p>
        }
        closeModal={() => setOptionToRemove(null)}
        showModal={!!optionToRemove}
        closeBtnText="Cancel"
        confirmBtnText="Delete Option"
        confirmBtnAction={confirmRemoveEditOption}
      />

      {/* delete field confirmation */}
      <ModalWidget
        title={removeField ? `Delete "${removeField.label}"?` : "Delete field?"}
        modalBody={
          <div style={{ fontSize: 15 }}>
            {removeField && (
              <>
                <p className="mb-0">
                  Deleting removes the field and clears its saved data from the
                  company records, and unbinds it from the groups
                  {removeField.usedIn > 0
                    ? " and unbinds it from those groups"
                    : ""}{" "}
                  — this cannot be undone.
                </p>
              </>
            )}
          </div>
        }
        closeModal={() => setRemoveField(null)}
        showModal={!!removeField}
        closeBtnText="Cancel"
        confirmBtnText="Delete Permanently"
        confirmBtnAction={() => removeField && deleteField(removeField)}
      />

      <div className="row gy-5 gx-xl-8">
        <div className="col-xxl-12">
          <div className="card card-custom">
            <div className="card-header">
              <div className="card-title d-flex flex-column align-items-start">
                <h3 className="fw-bolder m-0">
                  {intl.formatMessage({ id: "MENU.PROFILEFIELDSLIBRARY" })}
                </h3>
                <span
                  className="text-muted mt-1"
                  style={{ fontSize: 13, fontWeight: 400 }}
                >
                  Define each field once — its label, format and options. Groups
                  compose their forms from these definitions, so a field stays
                  consistent everywhere it's used.
                </span>
              </div>
              <div className="card-toolbar">
                <button
                  className={`btn ${
                    showAddField ? "btn-light" : "btn-ll"
                  } my-4`}
                  onClick={() => setShowAddField((v) => !v)}
                >
                  {showAddField ? "Close" : "+ New Field"}
                </button>
              </div>
            </div>

            <div className="card-body">
              {/* add-new-field form */}
              {showAddField && (
                <div
                  className="mb-6 p-4"
                  style={{
                    border: "1px dashed #bfdedb",
                    borderRadius: 6,
                    background: "#fafbfb",
                  }}
                >
                  <div
                    style={{
                      fontSize: 12.5,
                      color: "#7b848d",
                      fontWeight: 700,
                      marginBottom: 10,
                    }}
                  >
                    New field
                  </div>
                  <div className="d-flex gap-3 align-items-center flex-wrap">
                    <input
                      className="form-control form-control-sm"
                      style={{ maxWidth: 300 }}
                      placeholder="Field label"
                      value={newLabel}
                      onChange={(e) => setNewLabel(e.target.value)}
                    />
                    <select
                      className="form-select form-select-sm"
                      style={{ maxWidth: 200 }}
                      value={newType}
                      onChange={(e) => {
                        setNewType(e.target.value as FieldType);
                        setNewOptions([]);
                      }}
                    >
                      {CUSTOM_TYPES.map((t) => (
                        <option key={t.value} value={t.value}>
                          {t.label}
                        </option>
                      ))}
                    </select>
                    <button className="btn btn-sm btn-ll" onClick={addField}>
                      Add Field
                    </button>
                  </div>

                  {isDropdown(newType) && (
                    <div className="d-flex align-items-center flex-wrap gap-2 mt-3">
                      <span
                        style={{
                          fontSize: 11.5,
                          color: "#9aa1a8",
                          fontWeight: 500,
                        }}
                      >
                        Options:
                      </span>
                      {newOptions.map((o) => (
                        <span key={o} className="pfl-option-chip">
                          {o}
                          <span
                            className="pfl-chip-x"
                            onClick={() =>
                              setNewOptions(newOptions.filter((x) => x !== o))
                            }
                          >
                            ×
                          </span>
                        </span>
                      ))}
                      <input
                        className="form-control form-control-sm"
                        style={{ width: 140 }}
                        placeholder="Add option"
                        value={newOptNew}
                        onChange={(e) => setNewOptNew(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && addNewOption()}
                      />
                      <button
                        className="btn btn-sm btn-light-primary"
                        onClick={addNewOption}
                      >
                        Add
                      </button>
                    </div>
                  )}

                  <p
                    style={{
                      margin: "10px 0 0",
                      fontSize: 11.5,
                      color: "#a4abb2",
                    }}
                  >
                    Choose the format now — it can't be changed once the field
                    exists.
                  </p>
                </div>
              )}

              {/* search */}
              <div className="mb-4" style={{ maxWidth: 320 }}>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Search field name"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>

              {/* table */}
              <table className="table align-middle gs-0 gy-4">
                <thead>
                  <tr style={{ verticalAlign: "middle" }}>
                    <th
                      style={{
                        width: "40%",
                        cursor: "pointer",
                        userSelect: "none",
                      }}
                      title="Sort alphabetically"
                      onClick={toggleSort}
                    >
                      Field{" "}
                      <span
                        className={`pfl-sort-arrow ${sortDir ? "active" : ""}`}
                      >
                        {sortDir === "asc"
                          ? "▲"
                          : sortDir === "desc"
                          ? "▼"
                          : "⇅"}
                      </span>
                    </th>
                    <th style={{ width: "25%" }}>Format</th>
                    <th style={{ width: "25%", textAlign: "right" }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {loader && <TableLoader rows={5} columns={3} />}
                  {!loader && rows.length === 0 && (
                    <tr>
                      <td colSpan={3}>
                        <b>No fields match your search.</b>
                      </td>
                    </tr>
                  )}
                  {!loader &&
                    rows.map((field) => (
                      <React.Fragment key={field.key}>
                        <tr
                          className={
                            field.deprecated ? "pfl-row-deprecated" : ""
                          }
                          style={{ verticalAlign: "middle" }}
                        >
                          <td>
                            <div className="d-flex align-items-center">
                              <span
                                style={{
                                  fontSize: 14,
                                  color: "#2f353c",
                                  fontWeight: 500,
                                }}
                              >
                                {field.label}
                              </span>
                            </div>
                            {field.builtIn && (
                              <div className="pfl-option-summary">
                                Always captured on every company.
                              </div>
                            )}
                            {optionSummary(field) && (
                              <div className="pfl-option-summary">
                                {optionSummary(field)}
                              </div>
                            )}
                          </td>
                          <td>
                            <span className="pfl-type-badge">
                              {typeLabel(field.type)}
                            </span>
                          </td>
                          <td style={{ textAlign: "right" }}>
                            {!field.system && !field.builtIn && (
                              <>
                                <button
                                  className="btn btn-sm actionButton btn-round btn-orange mt-2"
                                  title="Edit"
                                  onClick={() => openEdit(field)}
                                >
                                  <i className="bi bi-pencil-fill fs-3"></i>
                                </button>
                                {field.deprecated ? (
                                  <button
                                    className="btn btn-sm actionButton btn-round btn-ll mt-2"
                                    title="Reactivate"
                                    onClick={() => reactivate(field)}
                                  >
                                    <i className="bi bi-arrow-counterclockwise fs-3"></i>
                                  </button>
                                ) : (
                                  <button
                                    className="btn btn-sm actionButton btn-round btn-red mt-2"
                                    title="Delete or archive"
                                    onClick={() => setRemoveField(field)}
                                  >
                                    <i className="bi bi-trash fs-3"></i>
                                  </button>
                                )}
                              </>
                            )}
                            {field.builtIn && (
                              <span
                                className="pfl-status-badge pfl-status-builtin"
                                title="Built-in company field — always captured, cannot be edited or removed"
                              >
                                BUILT-IN
                              </span>
                            )}
                            {field.system && !field.builtIn && (
                              <span className="pfl-status-badge pfl-status-system">
                                SYSTEM
                              </span>
                            )}
                          </td>
                        </tr>
                        {editKey === field.key && renderEditPanel(field)}
                      </React.Fragment>
                    ))}
                </tbody>
              </table>
            </div>
            {/* Save / Cancel — persists all pending add/edit/delete changes at once */}
            <div className="d-flex justify-content-center gap-3 my-8">
              <button
                type="button"
                className="btn btn-light"
                onClick={handleCancel}
                disabled={busy}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-ll"
                onClick={handleSave}
                disabled={busy || loader}
              >
                {busy ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export { ProfileFieldsLibrary };
